import type { IObjectSchema, Joi } from '@ksyos/koi';
import { Koi } from '@ksyos/koi';
import { isEqual, set } from 'lodash';
import type { CustomControlCode } from '@common/enums';
import type { DependsOn } from '@common/formEngine/dependsOn';
import type { TypedSchema } from '@common/validation/utils/typedSchemaType';
import { isDependsOn } from '@common/formEngine/dependsOn';
import { getInitialValue } from '@common/formEngine/typedSectionData';
import { never } from '@common/utils/never';
import { getProcessActivityAssetsSchema } from '@common/validation/processActivityAsset';
import { getQuantitySchema } from '@common/validation/quantity';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';
import { getTextInputStringSchema } from '@common/validation/utils/getTextInputStringSchema';
import type {
    CheckboxGroupInputField,
    InputField,
    InputFieldWithSectionAndSubsection,
    NestedCheckboxGroupWithTextInputField,
    Section,
} from './formEngine.types';
import { getInputFieldsFromSections } from './typedSections';

/**
 * Get schema for a field that has a list of options such as a radio group or select.
 * This can be used as replacement for `Koi.koi().enum()` as the form engine does not know the Enum value of a field.
 *
 * @param options List of options
 */
export function getOptionsSchema(options: string[]): Joi.AnySchema {
    return Koi.any()
        .valid(...options)
        .error((errors) =>
            errors.map((error) => {
                // TODO: CS-24724 Verify with UX whether this mapping to change the error message is necessary or not
                if (error.code === 'any.only') {
                    error.code = 'any.selected';
                }

                return error;
            })
        );
}

/**
 * Add include and required validation to a given schema for a field that depends on other data in the validation object.
 * The `includeFn` function is executed with the full data of the validation object to determine if the field should
 * be validated or not. If it should not be validated, it is assumed that the value MUST be the initialValue.
 * The `isRequired` function is executed with the full data of the validation object to determine if the field is required.
 * If the field is not required, the field is allowed to be valid according to the schema and the initial value.
 * It may be necessary to extend the error message mapping to support other types in the future.
 *
 * @param includeFn The function that determines if the field should be validated or not
 * @param initialValue The initial value of the field
 * @param isRequired The function that determines if the field is required
 * @param schema The schema to validate the field against
 */
export function addIncludeAndRequiredValidationToSchema<
    Data extends Record<string, unknown>,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>({
    includeFn,
    initialValue,
    isRequired,
    schema,
}: {
    includeFn?: DependsOn<Data, boolean, TCustomControlCode, Version>['fn'];
    initialValue?: null | [] | Record<string, unknown>;
    isRequired: boolean | DependsOn<Data, boolean, TCustomControlCode, Version>['fn'];
    schema: Joi.AnySchema;
}): Joi.AnySchema {
    if (!includeFn && isRequired === false) {
        return schema.allow(initialValue);
    }
    if (!includeFn && isRequired === true) {
        return schema;
    }

    return (
        Koi.any()
            .custom((value, helpers) => {
                // The full data object is always the last ancestor (relevant for nested fields)
                const data = helpers.state.ancestors.at(-1);

                if (includeFn?.(data) ?? true) {
                    let scopedSchema = schema;
                    const evaluatedIsRequired = typeof isRequired === 'function' ? isRequired(data) : isRequired;
                    if (!evaluatedIsRequired) {
                        scopedSchema = schema.allow(initialValue);
                    }
                    const { error } = scopedSchema.validate(value, { context: helpers.state });
                    if (error) {
                        return helpers.error(error.details[0].type, error.details[0].context);
                    }
                } else if (!isEqual(value, initialValue)) {
                    return helpers.error('any.only', { valids: [initialValue] });
                }

                return value;
            })
            // This mapping should be completed with all supported field types
            .messages({
                'boolean.base': '"{{#key}}" must be a boolean',
                'number.max': '"{{#key}}" must be smaller',
                'number.mix': '"{{#key}}" must be bigger',
                'string.base': '"{{#key}}" must be a string',
            })
    );
}

export function getEnhancedSchema<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(
    field: InputFieldWithSectionAndSubsection<Data, ContentKey, TCustomControlCode, Version>,
    schema: Joi.AnySchema,
    isDraft = false
): Joi.AnySchema {
    const { include, isRequired, section, subsection, type } = field;

    // Combine the include function of the field with the include function of the section and (optional) subsection.
    // This ensures that the field is only validated if both the field and the parent include functions return true.
    // The function returns true by default when the include function of the field, section, or subsection is not defined.
    const combinedIncludeFn: DependsOn<Data, boolean, TCustomControlCode, Version>['fn'] | undefined =
        include?.fn || section.include?.fn || subsection?.include?.fn
            ? (data) =>
                  (include?.fn(data) ?? true) &&
                  (section.include?.fn(data) ?? true) &&
                  (subsection?.include?.fn(data) ?? true)
            : undefined;

    // Determine if the field is required based on the `isRequired` field or the `dependsOn` function.
    // This results in either a boolean or a function. Draft schemas should never be required.
    let isRequiredBooleanOrFn: boolean | DependsOn<Data, boolean, TCustomControlCode, Version>['fn'] = false;
    if (!isDraft && typeof isRequired === 'boolean') {
        isRequiredBooleanOrFn = isRequired;
    } else if (!isDraft && isDependsOn<Data, boolean, TCustomControlCode, Version>(isRequired)) {
        isRequiredBooleanOrFn = isRequired.fn;
    }

    // Assets are a special case
    if (type === 'assets') {
        if (typeof isRequiredBooleanOrFn === 'function') {
            throw new Error('isRequired dependsOn function is not supported for assets field');
        }

        return isRequiredBooleanOrFn ? getProcessActivityAssetsSchema({ min: 1 }) : getProcessActivityAssetsSchema();
    }

    return addIncludeAndRequiredValidationToSchema<Data, TCustomControlCode, Version>({
        includeFn: combinedIncludeFn,
        initialValue: getInitialValue(field),
        isRequired: isRequiredBooleanOrFn,
        schema,
    });
}

// TODO: CS-24682 Implement for date/monthInput
export function getDefaultSchema<
    Data extends Record<string, unknown>,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(field: InputField<Data, TCustomControlCode, Version>, isDraft = false): Joi.AnySchema {
    const { isRequired = true, type } = field;

    switch (type) {
        case 'assets':
            return getProcessActivityAssetsSchema();
        case 'boolean':
            return Koi.boolean();
        case 'checkboxGroup':
            return getCheckboxGroupSchema(field, isDraft, Boolean(isRequired));
        case 'nestedCheckboxGroup':
            return getNestedCheckboxGroupSchema(field, isDraft);
        case 'icd10':
            return Koi.object({
                code: Koi.string(),
                description: Koi.string(),
            }).error((errors) =>
                errors.map((error) => {
                    // TODO: CS-24724 Verify with UX whether this mapping to change the error message is necessary or not
                    if (error.code === 'object.base') {
                        error.code = 'any.required';
                    }

                    return error;
                })
            );
        case 'number': {
            let numberSchema = Koi.number();
            if (field.minValue != null) {
                numberSchema = numberSchema.min(field.minValue);
            }
            if (field.maxValue != null) {
                numberSchema = numberSchema.max(field.maxValue);
            }

            return numberSchema;
        }
        case 'quantity':
            return getQuantitySchema({
                isDraft,
                isRequired: Boolean(field.isRequired),
                maxValue: field.maxValue,
                minValue: field.minValue,
            });
        case 'radioGroup':
            if (!Array.isArray(field.valueOptions)) {
                throw new Error('Get default schema currently only supports an array as value options');
            }

            return field.allowOtherOption ? getTextInputStringSchema() : getOptionsSchema(field.valueOptions);
        case 'select':
            if (!Array.isArray(field.valueOptions)) {
                throw new Error('Get default schema currently only supports an array as value options');
            }

            return getOptionsSchema(field.valueOptions);
        case 'textArea':
            return getTextAreaStringSchema();
        case 'textInput':
            return getTextInputStringSchema();
        case 'custom':
        case 'dateInput':
        case 'monthInput':
            throw new Error(`No default schema available for type "${type}" and key "${field.key}"`);
        /* c8 ignore start */
        default:
            never(type);
        /* c8 ignore stop */
    }
}

function getCheckboxGroupSchema<
    Data extends Record<string, unknown>,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(
    field: CheckboxGroupInputField<Data, TCustomControlCode, Version>,
    isDraft: boolean,
    isRequired: boolean
): IObjectSchema {
    let checkboxGroupSchema = Koi.object();
    for (const key of Object.keys(field.checkboxEnumType)) {
        checkboxGroupSchema = checkboxGroupSchema.append({
            [key]: Koi.boolean().allow(null),
        });
    }
    if (field.checkboxEnumType['anders']) {
        checkboxGroupSchema = checkboxGroupSchema.append({ anders: getTextInputStringSchema(true) });
    }

    if (field.hasTextInputOptions && Array.isArray(field.hasTextInputOptions)) {
        for (const option of field.hasTextInputOptions) {
            checkboxGroupSchema = checkboxGroupSchema.append({ [option]: getTextInputStringSchema(true) });
        }
    }

    if (!isDraft && isRequired) {
        checkboxGroupSchema = checkboxGroupSchema.anyFilledIn();
    }

    return checkboxGroupSchema;
}

function getNestedCheckboxGroupSchema<
    Data extends Record<string, unknown>,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(field: NestedCheckboxGroupWithTextInputField<Data, TCustomControlCode, Version>, isDraft: boolean): IObjectSchema {
    let checkboxGroupSchema = Koi.object();
    for (const key of Object.keys(field.checkboxEnumType)) {
        checkboxGroupSchema = checkboxGroupSchema.append({
            [key]: Koi.boolean().allow(null),
        });

        if (isTextInput(key, field)) {
            checkboxGroupSchema = checkboxGroupSchema.append({ [key]: getTextInputStringSchema(true) });
        }
    }

    if (!isDraft) {
        checkboxGroupSchema = addNestedCheckboxSchema(field, checkboxGroupSchema);
        checkboxGroupSchema = checkboxGroupSchema.anyFilledIn();
    }

    return checkboxGroupSchema;
}

function addNestedCheckboxSchema<
    Data extends Record<string, unknown>,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(
    field: NestedCheckboxGroupWithTextInputField<Data, TCustomControlCode, Version>,
    schema: IObjectSchema
): IObjectSchema {
    const optionsWithChildren = field.valueOptions.filter((opt) => opt.children && opt.children.length > 0);

    return schema.custom((value, helpers) => {
        for (const option of optionsWithChildren) {
            if (value[option.key] === true && option.children) {
                const childKeys = option.children.map((child) => child.key);

                const anyChildChecked = childKeys.some(
                    (key) => value[key] === true || (typeof value[key] === 'string' && value[key])
                );
                if (!anyChildChecked) {
                    return helpers.error('object.childAnyFilledIn');
                }
            }
        }

        return value;
    });
}

function isTextInput<
    Data extends Record<string, unknown>,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(key: string, field: NestedCheckboxGroupWithTextInputField<Data, TCustomControlCode, Version>): boolean {
    return field.valueOptions.some(
        (option) =>
            (option.key === key && option.isTextInput) ||
            option.children?.some((child) => child.key === key && child.isTextInput)
    );
}

function convertToDraftSchema<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(sections: Section<Data, ContentKey, TCustomControlCode, Version>[]): TypedSchema<Data> {
    return getInputFieldsFromSections<Data, ContentKey, TCustomControlCode, Version>(sections).reduce(
        (object, field) => {
            // TODO: CS-24959 Custom schema to make assets work with multiple category
            if (field.type === 'assets' && field.key !== 'assets' && !object['assets'] && field.category) {
                set(object, 'assets', getProcessActivityAssetsSchema());
            }

            let schema: Joi.AnySchema;
            if (field.draftSchema) {
                schema = field.draftSchema;
            } else {
                schema = getDefaultSchema(field, true);
                schema = getEnhancedSchema(field, schema, true);
            }
            set(object, field.key, schema);

            return object;
        },
        {} as TypedSchema<Data>
    );
}

export function convertToDraftObjectSchema<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(sections: Section<Data, ContentKey, TCustomControlCode, Version>[]): IObjectSchema {
    return Koi.object(convertToDraftSchema(sections));
}

function convertToFinalSchema<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(sections: Section<Data, ContentKey, TCustomControlCode, Version>[]): TypedSchema<Data> {
    return getInputFieldsFromSections(sections).reduce((object, field) => {
        // TODO: CS-24959 Custom schema to make assets work with multiple category fields
        if (field.type === 'assets' && field.key !== 'assets' && !object['assets'] && field.category) {
            set(object, 'assets', getProcessActivityAssetsSchema());
        }

        let schema: Joi.AnySchema;
        if (field.finalSchema) {
            schema = field.finalSchema;
        } else {
            schema = getDefaultSchema(field);
            schema = getEnhancedSchema(field, schema);
        }
        set(object, field.key, schema);

        return object;
    }, {} as TypedSchema<Data>);
}

export function convertToFinalObjectSchema<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(sections: Section<Data, ContentKey, TCustomControlCode, Version>[]): IObjectSchema {
    return Koi.object(convertToFinalSchema(sections));
}
